import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { headerStaysSignedIn } from '../utils/signupSessionGuards';

const HEADER_UID_KEY = 'prairi_uid';

function readStoredUid(): string | null {
  try {
    return localStorage.getItem(HEADER_UID_KEY);
  } catch {
    return null;
  }
}

function readSessionUserType(): 'startup' | 'investor' | null {
  try {
    const uid = readStoredUid();
    if (!uid) return null;
    const userType = localStorage.getItem(`prairi_ut_${uid}`);
    if (userType === 'startup' || userType === 'investor') return userType;
    return null;
  } catch {
    return null;
  }
}

// Header context types
interface HeaderContextType {
  // Mobile menu state
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;

  // Navigation state
  isLoggedIn: boolean;
  userName: string | null;
  /** Last signed-in account type, used only while auth is restoring so the nav does not flash the guest menu. */
  sessionUserType: 'startup' | 'investor' | null;
}

// Create context
const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

// Provider component
interface HeaderProviderProps {
  children: ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get auth state
  const { firebaseUser, loading } = useAuth();
  const [sessionUserType, setSessionUserType] = useState<'startup' | 'investor' | null>(
    readSessionUserType
  );

  // A signed-in header stays signed in while auth restores or while signup sets loading.
  // Treating `loading` as logged out flashed the guest menu (Startups / Log in) on every reload.
  const isLoggedIn = headerStaysSignedIn(firebaseUser !== null, loading, readStoredUid());
  const userName = firebaseUser?.email || null;

  React.useEffect(() => {
    if (loading) return;
    try {
      if (firebaseUser) localStorage.setItem(HEADER_UID_KEY, firebaseUser.uid);
      else localStorage.removeItem(HEADER_UID_KEY);
    } catch {
      // localStorage unavailable — the nav falls back to the live auth user
    }
    setSessionUserType(firebaseUser ? readSessionUserType() : null);
  }, [loading, firebaseUser]);

  // Mobile menu functions
  const toggleMobileMenu = React.useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = React.useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Context value
  const value: HeaderContextType = {
    isMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,
    isLoggedIn,
    userName,
    sessionUserType,
  };

  return <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>;
};

// Hook to use header context
export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};
